import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";

import { Portfolio } from "@/api/types";

export type SellOrderRow = {
  securityCode: string;
  securityId: number;
  name: string;
  class2code: string;
  holding: number;
  marketUnitPrice: number;
  sellQty: string;
  feePercent: string;
  type: "Market" | "Limit" | "";
  limitPrice: string;
  duration: string;
};

type Props = {
  selectedPortfolio?: Portfolio;
  savedRows?: SellOrderRow[];
  onChange?: (rows: SellOrderRow[]) => void;
};

function buildRows(portfolio?: Portfolio): SellOrderRow[] {
  if (!portfolio) return [];
  return portfolio.portfolioReport.portfolioReportItems
    .filter((item) => !item.security.isAccount && item.amount > 0)
    .map((item) => ({
      securityCode: item.security.securityCode,
      securityId: item.security.id,
      name: item.security.name,
      class2code: item.security.classType2?.code ?? "",
      holding: item.amount,
      marketUnitPrice: item.marketUnitPrice,
      sellQty: "",
      feePercent: "",
      type: "",
      limitPrice: "",
      duration: "",
    }));
}

function normalizeHolding(n: number): number {
  // Strip floating-point representation noise while keeping actual precision
  return parseFloat(n.toPrecision(12));
}

function mergeWithSaved(
  fresh: SellOrderRow[],
  saved: SellOrderRow[]
): SellOrderRow[] {
  if (!saved.length) return fresh;
  const savedMap = new Map(saved.map((r) => [r.securityCode, r]));
  return fresh.map((row) => {
    const s = savedMap.get(row.securityCode);
    if (!s) return row;
    return {
      ...row,
      sellQty: s.sellQty,
      feePercent: s.feePercent,
      type: s.type,
      limitPrice: s.limitPrice,
      duration: s.duration,
    };
  });
}

export function SellOrders({ selectedPortfolio, savedRows, onChange }: Props) {
  const prevPortfolioIdRef = useRef(selectedPortfolio?.id);

  const [rows, setRows] = useState<SellOrderRow[]>(() =>
    mergeWithSaved(buildRows(selectedPortfolio), savedRows ?? [])
  );

  // Rebuild rows when portfolio changes (skip initial mount)
  useEffect(() => {
    if (selectedPortfolio?.id === prevPortfolioIdRef.current) return;
    prevPortfolioIdRef.current = selectedPortfolio?.id;
    const next = buildRows(selectedPortfolio);
    setRows(next);
    onChange?.([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPortfolio?.id]);

  const updateSellQty = (securityCode: string, value: string) => {
    const next = rows.map((row) => {
      if (row.securityCode !== securityCode) return row;
      const raw = parseFloat(value) || 0;
      const maxQty = normalizeHolding(row.holding);
      const qty = Math.min(raw, maxQty);
      const clampedValue = value === "" ? "" : String(qty);
      return { ...row, sellQty: clampedValue };
    });
    setRows(next);
    onChange?.(next.filter((r) => parseFloat(r.sellQty) > 0));
  };

  const updateFeePercent = (securityCode: string, value: string) => {
    const next = rows.map((row) =>
      row.securityCode !== securityCode ? row : { ...row, feePercent: value }
    );
    setRows(next);
    onChange?.(next.filter((r) => parseFloat(r.sellQty) > 0));
  };

  const updateDuration = (securityCode: string, value: string) => {
    const next = rows.map((row) =>
      row.securityCode !== securityCode ? row : { ...row, duration: value }
    );
    setRows(next);
    onChange?.(next.filter((r) => parseFloat(r.sellQty) > 0));
  };

  const updateType = (securityCode: string, value: string) => {
    const next = rows.map((row) => {
      if (row.securityCode !== securityCode) return row;
      const updated = { ...row, type: value as SellOrderRow["type"] };
      if (value !== "Limit") {
        updated.limitPrice = "";
        updated.duration = "";
      }
      return updated;
    });
    setRows(next);
    onChange?.(next.filter((r) => parseFloat(r.sellQty) > 0));
  };

  const updateLimitPrice = (securityCode: string, value: string) => {
    const next = rows.map((row) =>
      row.securityCode !== securityCode ? row : { ...row, limitPrice: value }
    );
    setRows(next);
    onChange?.(next.filter((r) => parseFloat(r.sellQty) > 0));
  };

  const totalSell = rows.reduce(
    (sum, r) => sum + (parseFloat(r.sellQty) || 0) * r.marketUnitPrice,
    0
  );

  if (rows.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
          Sell orders
        </p>
        <p className="text-xs text-muted-foreground italic">
          {selectedPortfolio
            ? "No holdings in selected portfolio."
            : "Select a portfolio to view holdings."}
        </p>
      </div>
    );
  }

  const resetRows = () => {
    const today = dayjs().format("YYYY-MM-DD");
    const next = rows.map((row) => ({
      ...row,
      sellQty: "",
      type: "" as SellOrderRow["type"],
      limitPrice: "",
      duration: today,
    }));
    setRows(next);
    onChange?.([]);
  };

  const sellAll = () => {
    const next = rows.map((row) => ({
      ...row,
      sellQty: String(normalizeHolding(row.holding)),
      sellAmount: "",
    }));
    setRows(next);
    onChange?.(next.filter((r) => parseFloat(r.sellQty) > 0));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold text-foreground">Salgsordre</p>
          <p className="text-[10px] text-muted-foreground">
            Velg beholdning og angi salgsdetaljer.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={sellAll}
            className="text-xs px-2.5 py-1 rounded-md border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            Selg alt
          </button>
          <button
            type="button"
            onClick={resetRows}
            className="text-xs px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            Nullstill
          </button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-muted/60 text-muted-foreground">
              {(
                [
                  { label: "Instrument", align: "left" },
                  { label: "Gebyr %", align: "left" },
                  { label: "Beholdning", align: "right" },
                  { label: "Kurs", align: "right" },
                  { label: "Markedsverdi", align: "right" },
                  { label: "Selg antall", align: "right" },
                  { label: "Type", align: "left" },
                  { label: "Limit kurs", align: "right" },
                  { label: "Varighet", align: "left", width: "80px" },
                ] as {
                  label: string;
                  align: "left" | "right";
                  width?: string;
                }[]
              ).map((h) => (
                <th
                  key={h.label}
                  className={`px-2 py-1.5 text-${h.align} font-medium border-b border-gray-200 dark:border-gray-700 whitespace-nowrap`}
                  style={h.width ? { width: h.width } : undefined}
                >
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.securityCode}
                className="border-b border-gray-100 dark:border-gray-800 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/50"
              >
                <td className="px-2 py-1.5 font-medium text-gray-800 dark:text-gray-200 max-w-[160px]">
                  <div className="truncate">{row.name}</div>
                  {row.securityCode && (
                    <div className="text-[10px] font-mono text-gray-400 mt-0.5">
                      {row.securityCode}
                    </div>
                  )}
                </td>
                <td className="px-1 py-1">
                  <input
                    type="number"
                    value={row.feePercent}
                    onChange={(e) =>
                      updateFeePercent(row.securityCode, e.target.value)
                    }
                    placeholder="0"
                    className="w-14 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </td>
                <td className="px-2 py-1.5 tabular-nums text-right">
                  {row.holding.toLocaleString("nb-NO", {
                    maximumFractionDigits: 4,
                  })}
                </td>
                <td className="px-2 py-1.5 tabular-nums text-right">
                  {row.marketUnitPrice.toLocaleString("nb-NO", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 4,
                  })}
                </td>
                <td className="px-2 py-1.5 tabular-nums text-right">
                  {(row.holding * row.marketUnitPrice).toLocaleString("nb-NO", {
                    maximumFractionDigits: 0,
                  })}
                </td>
                <td className="px-1 py-1 text-right">
                  <input
                    type="number"
                    value={row.sellQty}
                    onChange={(e) =>
                      updateSellQty(row.securityCode, e.target.value)
                    }
                    placeholder="0"
                    min={0}
                    max={normalizeHolding(row.holding)}
                    step="any"
                    className="w-20 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1.5 py-0.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </td>
                <td className="px-1 py-1">
                  {["FI", "EF"].includes(row.class2code) ? (
                    <span className="text-xs text-gray-400 italic px-1">
                      N/A
                    </span>
                  ) : (
                    <select
                      value={row.type}
                      onChange={(e) =>
                        updateType(row.securityCode, e.target.value)
                      }
                      disabled={!(parseFloat(row.sellQty) > 0)}
                      className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 pl-1.5 pr-6 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="" disabled>
                        -- Velg --
                      </option>
                      <option value="Market">Market</option>
                      <option value="Limit">Limit</option>
                    </select>
                  )}
                </td>
                <td className="px-1 py-1 text-right">
                  <input
                    type="number"
                    value={row.limitPrice}
                    onChange={(e) =>
                      updateLimitPrice(row.securityCode, e.target.value)
                    }
                    placeholder="—"
                    disabled={row.type !== "Limit"}
                    className="w-20 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1.5 py-0.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800"
                  />
                </td>
                <td className="px-1 py-1">
                  <input
                    key={`duration-${row.securityCode}`}
                    type="date"
                    value={row.duration}
                    onChange={(e) =>
                      updateDuration(row.securityCode, e.target.value)
                    }
                    disabled={row.type !== "Limit"}
                    className="w-28 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800"
                  />
                </td>
              </tr>
            ))}
          </tbody>
          {totalSell > 0 && (
            <tfoot>
              <tr className="bg-gray-50 dark:bg-gray-800/60 font-semibold">
                <td
                  colSpan={8}
                  className="px-2 py-1.5 text-right text-xs text-gray-600 dark:text-gray-400"
                >
                  Totalt salg:
                </td>
                <td className="px-2 py-1.5 tabular-nums text-right text-blue-700 dark:text-blue-400 text-xs">
                  {totalSell.toLocaleString("nb-NO", {
                    maximumFractionDigits: 0,
                  })}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
